/**
 * LocationHero Component
 *
 * Displays the hero section for a restaurant location page. Includes:
 * - Location title, description, and area information
 * - Address, phone number, and hours
 * - Call-to-action buttons (reservations, ordering, waitlist)
 * - Featured image carousel
 * - Email signup for coming soon / soft opening locations
 * - Status badges (new, coming soon, soft opening, reservation only, etc.)
 *
 * Manages:
 * - Yelp waitlist data fetching (when location is open and not reservation-only)
 * - Conditional rendering based on location status (open, coming soon, soft opening, etc.)
 * - Restaurant state (active location) in global store
 * - Internationalization for multi-language support
 */

import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'

import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs'
import Button from '@/components/Button/Button'
import EmailSignupInput from '@/components/EmailSignupInput/EmailSignupInput'
import RichTextContentRichText from '@/sections/RichTextContent/RichTextContentRichText'

import { DOC_TYPES } from '@/data'

import useBreakpoint from '@/hooks/use-breakpoint'
import useI18n from '@/hooks/use-i18n'

import LocationHeroImageCarousel from '@/sections/LocationHero/LocationHeroImageCarousel/LocationHeroImageCarousel'
import LocationHeroHoursNewFormat from '@/sections/LocationHero/LocationHeroHoursNewFormat/LocationHeroHoursNewFormat'
import LocationActionButtons from '@/components/LocationActionButtons/LocationActionButtons'

import useStore from '@/store'

import { getRestaurantAddressString } from '@/utils/format'
import {
  getLocationListItemId,
  getMergedTimeRangeHoursWithTimeRangeHolidayHours,
  getIfRestaurantIsOpenNewFormat,
} from '@/utils/restaurant'

import styles from './LocationHero.module.scss'

/** HTML ID for newsletter input field - used for analytics and form identification */
export const LOCATION_HERO_INPUT_ID = 'locationHeroInputNewsletter'

import { type LocationHero } from '@/types/sections/LocationHero'
import { ParsedResponse } from '@/types/yelp'
import { extractYelpBusinessId } from '@/utils/yelp'
import Icon from '@/components/Icon/Icon'

/**
 * Validates that a business ID has the expected format
 * (alphanumeric, hyphens, and underscores only)
 */
const isValidBusinessId = (id: string): boolean => {
  return /^[a-zA-Z0-9_-]+$/.test(id)
}

const LocationHeroSection = (props: LocationHero) => {
  // Destructure location data from Sanity content
  const {
    areaTitle,
    city,
    emailSubscribeText,
    eyebrowText,
    featuredImage,
    googleMapsLink,
    cateringLink,
    hasCatering,
    heroDescription,
    holidayHours,
    images,
    isComingSoon,
    isReservationOnly,
    isSoftOpening,
    isTemporarilyClosed,
    isNew,
    phoneNumber,
    postalCode,
    province,
    reservationLinks,
    serviceLinks,
    state,
    streetAddress,
    timeZone,
    title,
    weeklyOpeningHours,
  } = props
  // Yelp waitlist state management
  const [waitlistData, setWaitlistData] = useState<ParsedResponse | null>(null)
  const [isLoadingWaitlist, setIsLoadingWaitlist] = useState(false)
  const [hasLoadedWaitlist, setHasLoadedWaitlist] = useState(false)

  // Extract Yelp business ID from first reservation link (used for waitlist queries)
  const businessId = extractYelpBusinessId(props.reservationLinks?.[0]?.url || '') ?? undefined

  // Hooks for internationalization, global state, and responsive design
  const { i18n, isValidI18nKey } = useI18n()
  const setActiveRestaurant = useStore(state => state.setActiveRestaurant)
  const { isMobile } = useBreakpoint()

  // Merge regular hours with holiday-specific hours to determine actual operating hours
  const adjustedHours = weeklyOpeningHours
    ? getMergedTimeRangeHoursWithTimeRangeHolidayHours(weeklyOpeningHours, holidayHours)
    : undefined

  // Check if restaurant is currently open based on timezone and adjusted hours
  const isRestaurantOpen =
    weeklyOpeningHours && timeZone && adjustedHours ? getIfRestaurantIsOpenNewFormat(timeZone, adjustedHours) : false

  // Format address string - use province for Canada, state for US
  const addressString = getRestaurantAddressString({
    streetAddress,
    city,
    state: process.env.NEXT_PUBLIC_IS_CANADA_WEBSITE === 'true' ? province : state,
    postalCode,
  })

  // Build breadcrumb navigation: Home -> State/Area -> Current Location
  const breadcrumbLinks: SanityLink[] = [
    {
      label: i18n('all'),
      linkType: 'internal',
      link: {
        _type: DOC_TYPES.PAGE,
        slug: 'locations',
      },
    },
    {
      label: isValidI18nKey(state) ? i18n(state) : '',
      linkType: 'internal',
      link: {
        _type: DOC_TYPES.PAGE,
        slug: 'locations',
      },
      hash: getLocationListItemId(state as string),
    },
  ]

  /**
   * Fetch Yelp waitlist data for the restaurant
   *
   * Only fetches if:
   * - Location has a valid business ID (validated format)
   * - Location hasn't been loaded yet (prevents duplicate fetches)
   * - Location is not reservation-only
   * - Location is currently open
   *
   * Silently fails if any preconditions aren't met (waitlist not critical)
   */
  const loadWaitlist = useCallback(async () => {
    // Prevent fetching if no business ID, already loaded, or business ID is invalid
    if (!businessId || !isValidBusinessId(businessId) || hasLoadedWaitlist) return

    // Skip waitlist for reservation-only or closed locations (early return instead of throw)
    if (isReservationOnly || !isRestaurantOpen) {
      setHasLoadedWaitlist(true)
      return
    }

    setIsLoadingWaitlist(true)

    try {
      // Fetch waitlist data from backend API
      const response = await fetch(`/api/yelp/${businessId}?location=${businessId}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Validate response has expected structure (waitEstimates is a record of strings)
      if (data && typeof data === 'object' && (data.waitEstimates === undefined || typeof data.waitEstimates === 'object')) {
        setWaitlistData(data as ParsedResponse)
      } else {
        console.warn('Invalid waitlist response structure')
        setWaitlistData(null)
      }
    } catch (error) {
      console.error('Error fetching waitlist data:', error)
      setWaitlistData(null)
    } finally {
      // Always mark as loaded, even on error (prevents endless retry attempts)
      setIsLoadingWaitlist(false)
      setHasLoadedWaitlist(true)
    }
  }, [businessId, hasLoadedWaitlist, isReservationOnly, isRestaurantOpen])

  /**
   * Determine eyebrow text to display above location title
   *
   * Priority:
   * 1. Use provided eyebrowText if available
   * 2. Fall back to "Coming Soon" if location is upcoming
   * 3. Fall back to "Soft Opening" if location is in soft opening phase
   * 4. Return null if no applicable text
   */
  const getEyebrowText = () => {
    if (!eyebrowText && isComingSoon) {
      return i18n('comingSoon')
    }

    if (!eyebrowText && isSoftOpening) {
      return i18n('softOpening')
    }

    if (!eyebrowText) {
      return null
    }

    return eyebrowText
  }

  // Update global store with current location when component mounts or location changes
  useEffect(() => {
    setActiveRestaurant(props)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setActiveRestaurant, title, city])

  // Load waitlist data when component mounts
  useEffect(() => {
    loadWaitlist()
  }, [loadWaitlist])

  // Prevent rendering if location title is missing (required for valid location)
  if (!title) return null

  return (
    <div className={styles.LocationHero}>
      <div className={styles.inner}>
        <div className={styles.textSide}>
          {/* Breadcrumbs navigation - only show on US site */}
          {process.env.NEXT_PUBLIC_IS_CANADA_WEBSITE !== 'true' && (
            <Breadcrumbs
              links={breadcrumbLinks}
              className={styles.breadcrumbs}
            />
          )}

          {/* "New Location" badge */}
          {isNew && (
            <div className={styles.newLocationBadge}>
              <Icon name="newLocationBadge" />
            </div>
          )}

          {/* Eyebrow text: custom text or auto-generated status (Coming Soon, Soft Opening) */}
          {(eyebrowText || isComingSoon || isSoftOpening) && <p className={styles.eyebrowText}>{getEyebrowText()}</p>}

          {/* Location title and area name */}
          <div className={styles.titleContainer}>
            <h1 className={styles.title}>{title}</h1>
            {areaTitle && (
              <p className={styles.areaTitle}>
                <span className={styles.areaTitle__at}>{i18n('at')}&nbsp;</span>
                <span className={styles.areaTitle__title}>{areaTitle}</span>
              </p>
            )}
          </div>

          {/* Rich text description (markdown-like content from Sanity) */}
          {heroDescription && (
            <div className={styles.heroDescription}>
              <RichTextContentRichText content={heroDescription} />
            </div>
          )}

          {/* ACTION BUTTONS - SOFT OPENING PHASE */}
          {isSoftOpening && (
            <LocationActionButtons
              reservationLinks={reservationLinks}
              serviceLinks={serviceLinks}
              businessId={businessId}
              isReservationOnly={isReservationOnly}
              waitlistData={waitlistData}
              isLoadingWaitlist={isLoadingWaitlist}
              isMobile={isMobile}
              hasCatering={hasCatering}
              cateringLink={cateringLink}
            />
          )}

          {/* EMAIL SIGNUP - COMING SOON / SOFT OPENING */}
          {/* Allow users to sign up for updates when location isn't open yet */}
          {(isComingSoon || isSoftOpening) && (
            <div
              className={classNames(styles.emailSignupContainer, {
                [styles.emailSignupContainerBorder]: isSoftOpening,
              })}
            >
              <p className={styles.emailSignupContainer__text}>
                {emailSubscribeText ? (
                  <span>{emailSubscribeText}</span>
                ) : (
                  'Enter your email below to receive updates about our new location.'
                )}
              </p>
              <EmailSignupInput
                className={styles.emailSignupContainer__emailInput}
                variant="simple"
                inputId={LOCATION_HERO_INPUT_ID}
              />
            </div>
          )}

          {/* ACTION BUTTONS - NORMAL/OPEN PHASE */}
          {!isComingSoon && !isSoftOpening && (
            <LocationActionButtons
              reservationLinks={reservationLinks}
              serviceLinks={serviceLinks}
              businessId={businessId}
              isReservationOnly={isReservationOnly}
              waitlistData={waitlistData}
              isLoadingWaitlist={isLoadingWaitlist}
              isMobile={isMobile}
              hasCatering={hasCatering}
              cateringLink={cateringLink}
            />
          )}

          {/* RESERVATION-ONLY BADGE */}
          {/* Alert badge if this location only accepts reservations */}
          {isReservationOnly && (
            <div className={styles.reservationOnly}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="9"
                height="10"
                viewBox="0 0 9 10"
                fill="none"
              >
                <path
                  d="M1.71571 9.28743C1.28273 8.9786 1.14743 8.36096 1.58041 7.79946C2.09457 7.15374 2.87934 6.64839 4.15121 5.4131C2.68991 5.35695 1.30979 5.27273 0.822695 5.13235C0.0920461 4.93583 -0.124443 4.4024 0.064985 3.81283C0.20029 3.25134 0.876817 2.97059 1.4451 3.22326C2.09457 3.44786 2.82522 4.03743 4.25945 4.93583C3.82647 3.19519 3.50174 2.40909 3.47468 1.56684C3.42056 0.893048 3.93472 0.5 4.503 0.5C5.07128 0.5 5.58545 0.893048 5.53132 1.56684C5.50426 2.40909 5.17953 3.25133 4.74655 4.93583C6.01842 4.14973 7.12793 3.39171 7.53384 3.19519C8.21037 2.88636 8.77865 3.25134 8.94102 3.81283C9.13045 4.43048 8.85984 4.93583 8.18331 5.10427C7.66915 5.24465 6.31609 5.32887 4.88186 5.4131C6.07255 6.42379 7.23617 7.37834 7.45266 7.74331C7.88564 8.38903 7.66915 8.9786 7.20911 9.25935C6.83026 9.51203 6.12667 9.56818 5.74781 8.89438C5.50426 8.50133 5.04422 7.32219 4.503 5.72192C3.93472 7.40641 3.50174 8.61363 3.25819 9.00668C2.87934 9.5401 2.20281 9.6524 1.71571 9.28743Z"
                  fill="#D7000F"
                />
              </svg>
              <span>{i18n('reservationOnly')}</span>
            </div>
          )}

          {/* CONTACT INFORMATION */}
          {/* Phone number and full address displayed side-by-side on tablet+ */}
          <div className={styles.contactMethodsContainer}>
            <div className={styles.contactMethodsContainer__left}>
              {phoneNumber && <p className={styles.phoneNumber}>{i18n('phoneNumberShort', { phoneNumber })}</p>}
              {addressString && (
                <p className={styles.address}>
                  {i18n('addressShort', {
                    address: addressString,
                  })}
                </p>
              )}
            </div>
            <div className={styles.contactMethodsContainer__right}>
              {/* "Find us on Maps" button with link to Google Maps */}
              {googleMapsLink && (
                <Button
                  link={{
                    label: i18n('findUs'),
                    linkType: 'external',
                    link: googleMapsLink,
                  }}
                  plain
                  icon="caretRight"
                />
              )}
            </div>
          </div>

          {/* HOURS OF OPERATION */}
          {/* Detailed hours display (hidden for coming soon / soft opening locations) */}
          {!isComingSoon && !isSoftOpening && (
            <div className={styles.hoursContainer}>
              {weeklyOpeningHours && timeZone && (
                <LocationHeroHoursNewFormat
                  weeklyOpeningHours={weeklyOpeningHours}
                  holidayHours={holidayHours}
                  timeZone={timeZone}
                  isTemporarilyClosed={isTemporarilyClosed}
                />
              )}
            </div>
          )}
        </div>

        {/* FEATURED IMAGE CAROUSEL */}
        {/* Image carousel shown on right side of layout (on tablet and up) */}
        <div className={styles.imageSide}>
          {featuredImage && <LocationHeroImageCarousel images={[featuredImage, ...(images as SanityImage[])]} />}
        </div>
      </div>
    </div>
  )
}

LocationHeroSection.displayName = 'LocationHero'

export default LocationHeroSection
